import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import colors from "styles/colors";
import Icon from "components/Icon";
import facebookActions from "modules/facebook/actions";
import Row from "components/Row";
import Column from "components/Column";
import googleActions from "modules/google/actions";

const AccountBox = ({ imageLink, name, accountType, leadType }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const rmpToken = sessionStorage.getItem("rmp_token");
  const [isOptionsVisible, setOptionsVisible] = useState(false);

  const textRef = useRef(null);
  const [isAccountNameOverflowing, setIsAccountNameOverflowing] = useState(false);

  useEffect(() => {
    const textElement = textRef.current;

    if (textElement) {
      const isAccountNameOverflowing = textElement.scrollWidth > textElement.clientWidth;
      setIsAccountNameOverflowing(isAccountNameOverflowing);
    }
  }, [textRef]);

  /** open/close disconnect dropdown */
  const handleClickDisConnected = () => {
    setOptionsVisible(!isOptionsVisible);
  };

  /** get facebook info after logout */
  const getFacebookUserInfo = async () => {
    await dispatch(
      facebookActions.getFacebookUserInfo({
        token: rmpToken,
        userAccessToken: null,
      }),
    );
    dispatch(
      facebookActions.getFacebookPageSetting({
        token: rmpToken,
        id: null,
      }),
    );
    await dispatch(facebookActions.setValidateFacebookLeadForm({ result: false }));
    await dispatch(facebookActions.changePageUrlInput(""));
    await dispatch(facebookActions.changePageFormSelectType(""));
    await dispatch(facebookActions.setCheckedFormItems(""));
  };

  /** facebook logout action */
  const facebookLogout = () => {
    FB.getLoginStatus(function (response) {
      if (response.status === "connected") {
        FB.logout(function (response) {
          getFacebookUserInfo();
        });
      } else {
        getFacebookUserInfo();
      }
    });
  };

  /** get google token by code = null (for logout google) */
  const googleExchangeToken = async () => {
    await dispatch(googleActions.setValidateGoogleSheetsUrl({ result: false }));
    await dispatch(googleActions.setValidateGGLeadsGoogleSheetsUrl({ result: false }));
    await dispatch(
      googleActions.getGoogleExchangeToken({
        token: rmpToken,
        code: null,
      }),
    );
    if (leadType === "facebook") {
      await dispatch(
        facebookActions.getGoogleSetting({
          token: rmpToken,
          id: null,
        }),
      );
      await dispatch(facebookActions.changeSheetsUrlInput(""));
    } else if (leadType === "google") {
      await dispatch(
        googleActions.getGoogleSheetsSetting({
          token: rmpToken,
          id: null,
        }),
      );
      await dispatch(googleActions.changeGoogleLeadSheetsUrlInput(""));
    }
  };

  /** action when click disconnect */
  const onDropdownItemSelected = async () => {
    handleClickDisConnected();
    if (accountType === "facebook") {
      facebookLogout();
      await dispatch(facebookActions.setFbDisconnected(true));
    } else if (accountType === "google") {
      googleExchangeToken();
      if (leadType === "facebook") {
        await dispatch(facebookActions.setGoogleDisconnected(true));
      } else if (leadType === "google") {
        await dispatch(googleActions.setGoogleLeadGoogleDisconnected(true));
      }
    }
  };

  /** item in dropdown */
  const DropdownItem = ({ item, index, onClick }) => {
    return (
      <StyledItem onClick={() => onClick(item, index)}>
        <div className="disconnected-text">{item.title}</div>
      </StyledItem>
    );
  };

  let items = [{ status: "change_connection", title: t("__disconnected") }];
  const viewItems = () =>
    items.map((item, index) => (
      <DropdownItem key={index} item={item} index={index} onClick={() => onDropdownItemSelected()}></DropdownItem>
    ));

  return (
    <Container>
      <AccountContainer
        alignItems="center"
        columnSpacing="8px"
        minWidth="100%"
        isOverflowing={isAccountNameOverflowing}
        onClick={() => handleClickDisConnected()}
      >
        <Column width="100%">
          <AccountContent>
            <AccountImage src={imageLink != null ? imageLink : "/images/person.svg"} />
            <AccountName isOverflowing={isAccountNameOverflowing} ref={textRef}>
              {name}
            </AccountName>
          </AccountContent>
        </Column>
        <Column wrap>
          <Icon name="icon-down-dir" size="14px" color="#555" />
        </Column>
      </AccountContainer>
      <ItemsContainer isVisible={isOptionsVisible} position={{ top: "112%" }} minWidth={"100%"}>
        {viewItems()}
      </ItemsContainer>
      {isOptionsVisible && <Backdrop onClick={() => setOptionsVisible(false)} />}
    </Container>
  );
};

export default AccountBox;

const Container = styled.div`
  position: relative;

  > *:first-child {
    cursor: pointer;
  }
`;

const AccountContainer = styled(Row).attrs((props) => ({
  minWidth: props.minWidth,
  opacity: props.disabled ? 0.75 : 1,
  height: props.isOverflowing ? "fit-content" : "45px",
}))`
  font-size: 14px;
  border: 1px solid #ddd;
  padding: 4px 8px 4px 16px;
  border-radius: 6px;
  background-color: #fff;
  box-sizing: border-box;
  min-width: ${(props) => props.minWidth};
  opacity: ${(props) => props.opacity};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")} !important;
  height: ${(props) => props.height};
`;

const AccountContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`;
const AccountImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
`;
const AccountName = styled.div.attrs((props) => ({
  lineHeight: props.isOverflowing ? "1rem" : "",
  wrap: props.isOverflowing ? "wrap" : "nowrap",
  wordBreak: props.isOverflowing ? "break-word" : "",
}))`
  margin-left: 10px;
  width: 280px;
  white-space: nowrap;
  line-height: ${(props) => props.lineHeight};
  white-space: ${(props) => props.wrap};
  word-break: ${(props) => props.wordBreak};
`;

const ItemsContainer = styled.div.attrs((props) => ({
  display: props.isVisible ? "block" : "none",
  top: props.position ? props.position.top : "35px",
  right: props.position && props.position.right ? props.position.right : null,
  minWidth: props.minWidth,
}))`
  display: ${(props) => props.display};
  position: absolute;
  border: 1px solid #ddd;
  background-color: ${colors.backgroundSection};
  box-shadow: ${colors.sectionShadow} 0 10px 30px;
  min-width: 150px;
  border-radius: 8px;
  z-index: 999;
  top: ${(props) => props.top};
  right: ${(props) => props.right};
  min-width: ${(props) => props.minWidth};
  padding: 8px 0px;
  text-align: left;
  max-height: 170px;
  overflow: auto;

  @media screen and (max-width: 834px) {
    box-shadow: none;
    width: calc(100% - 32px);
    position: fixed;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    margin: 16px;
    text-align: center;
    max-height: calc(100vh - 150px);
    min-width: unset;

    p {
      font-size: 14px;
    }
  }
`;

const StyledItem = styled.div`
  cursor: pointer;

  .disconnected-text {
    font-size: 14px;
    color: #555;
    padding: 8px 16px;
    line-height: 1rem;
  }

  @media screen and (max-width: 834px) {
    padding: 16px;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 998;

  @media screen and (max-width: 834px) {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;
